import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { IconComponent } from '@shared/components/ui/icon/icon.component';
import { NotificationService, Toast, ToastType } from '../../../core/services/notification.service';

interface KxToastView {
  id: string;
  type: string;
  title: string;
  msg: string;
  icon: string;
  dur: number;
  persistent: boolean;
  leaving: boolean;
  paused: boolean;
}

@Component({
  selector: 'app-toaster',
  standalone: true,
  imports: [IconComponent],
  template: `
  @if (toasts().length) {
    <div class="kx-toast-wrap">
      @for (t of toasts(); track t.id) {
        <div
          class="kx-toast"
          [attr.data-type]="t.type"
          [attr.data-leaving]="t.leaving || null"
          [attr.data-paused]="t.paused || null"
          (mouseenter)="pause(t.id)"
          (mouseleave)="resume(t.id)"
        >
          <span class="kx-toast-ic">
            <app-icon [name]="t.icon" [size]="17"></app-icon>
          </span>

          <div class="kx-toast-body">
            <div class="kx-toast-title">{{ t.title }}</div>

            @if (t.msg) {
              <div class="kx-toast-msg">{{ t.msg }}</div>
            }
          </div>

          <button
            class="kx-toast-x"
            type="button"
            aria-label="Dismiss"
            (click)="close(t.id)"
          >
            <app-icon name="x" [size]="15"></app-icon>
          </button>

          @if (!t.persistent && t.dur > 0) {
            <div
              class="kx-toast-prog"
              [style.--dur]="t.dur + 'ms'"
              [class.paused]="t.paused"
            ></div>
          }
        </div>
      }
    </div>
  }
`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToasterComponent implements OnInit, OnDestroy {
  private readonly service = inject(NotificationService);
  readonly toasts = signal<KxToastView[]>([]);

  private subscription?: Subscription;
  private readonly leavingTimers = new Map<string, ReturnType<typeof setTimeout>>();

  ngOnInit(): void {
    this.subscription = this.service.activeToasts$.subscribe((list) => this.sync(list));
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.leavingTimers.forEach((timer) => clearTimeout(timer));
    this.leavingTimers.clear();
  }

  close(id: string): void {
    this.service.dismiss(id);
  }

  pause(id: string): void {
    this.toasts.update((list) =>
      list.map((t) => (t.id === id ? { ...t, paused: true } : t))
    );

    this.service.pause(id);
  }

  resume(id: string): void {
    this.toasts.update((list) =>
      list.map((t) => (t.id === id ? { ...t, paused: false } : t))
    );

    this.service.resume(id);
  }

  private sync(incoming: Toast[]): void {
    const incomingIds = new Set(incoming.map((t) => t.id));
    const current = this.toasts();

    // Preserve paused state for existing toasts
    const next: KxToastView[] = incoming.map((toast) => {
      const existing = current.find((v) => v.id === toast.id);

      return {
        ...this.toView(toast),
        paused: existing?.paused ?? false,
      };
    });

    // Handle leaving toasts
    for (const view of current) {
      if (incomingIds.has(view.id)) {
        continue;
      }

      next.push({
        ...view,
        leaving: true,
        paused: false,
      });

      if (!this.leavingTimers.has(view.id)) {
        const timer = setTimeout(() => {
          this.leavingTimers.delete(view.id);
          this.toasts.update((list) => list.filter((v) => v.id !== view.id));
        }, 220);

        this.leavingTimers.set(view.id, timer);
      }
    }

    this.toasts.set(next);
  }

  private toView(toast: Toast): KxToastView {
    return {
      id: toast.id,
      type: toast.type,
      title: toast.title ?? '',
      msg: toast.message,
      icon: this.iconFor(toast),
      dur: toast.duration ?? 0,
      persistent: !!toast.persistent,
      leaving: false,
      paused: false,
    };
  }

  private iconFor(toast: Toast): string {
    if (toast.icon === 'spinner') {
      return 'refresh';
    }
    switch (toast.type) {
      case ToastType.SUCCESS:
        return 'check';
      case ToastType.WARNING:
        return 'alert';
      case ToastType.ERROR:
        return 'x';
      default:
        return 'info';
    }
  }
}
